import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { register, resendVerification } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { GTMEvent, emailVerificationMeta, loginSuccessMeta } from '../../../hooks/gtmHelpers'
import { getUserByToken } from '../core/_requests'
import TagManager from 'react-gtm-module'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import { CustomToast } from '../../widgets/components/UI/CustomToast'
import TextInput from '../../widgets/components/Input/TextInput'
import { CustomButton } from '../../widgets/components/UI/CustomButton'
import { Modal } from 'react-bootstrap'
import { removeNullFields } from '../../../core/_util'
import brandingConfig from '../../../../config/brandingConfig'
import { getLocation } from '../../onboarding/core/_requests'

export const VerifyRegistration = () => {
  const { userId } = useParams()
  const numericUserId = parseInt(userId!, 10)
  const { saveAuth, setUserToken, setNewCompany } = useAuth()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(false)
  const [errorsArray, setErrorsArray] = useState<string[]>()
  const [email, setEmail] = useState<string>()
  const [otpTimer, setOtpTimer] = useState(120)
  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [otpWaitModalShow, setOtpWaitModalShow] = useState<boolean>(false)
  const [otpResentModalShow, setOtpResentModalShow] = useState<boolean>(false)
  const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false)
  const [ipStackData, setIpStackData] = useState<any>()
  const isRecordskeeper = process.env.REACT_APP_BRAND === 'RK'

  const otpInitialValues = {
    otp: '',
  }

  const verifyOtpSchema = Yup.object().shape({
    otp: Yup.string()
      .matches(/^\d{6}$/, 'OTP must be a 6-digit number')
      .required('OTP is required'),
  })

  useEffect(() => {
    // Retrieve email from local storage
    const storedEmail = localStorage.getItem('email')
    if (storedEmail) {
      setEmail(storedEmail)
    }
  }, [userId])

  useEffect(() => {
    const fetchIpStackData = async () => {
      try {
        const ipStackData = await getLocation()
        setIpStackData(ipStackData.data)
      } catch (err) {
        console.log(err)
      }
    }
    fetchIpStackData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOtpVerify = async (values: any, { setSubmitting }: any) => {
    try {
      setLoading(true)
      const {
        data: { data, success, errors },
      } = await register(numericUserId, values.otp)
      if (success) {
        setErrorsArray([])
        setUserToken(data.token)
        localStorage.setItem('signUpCompany', data.signUpCompany)

        if (isRecordskeeper) {
          navigate('/auth/info/')
        } else {
          navigate('/onboarding/')
        }

        const { data: userData } = await getUserByToken(data.token)

        await saveAuth(data)

        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.EmailVerificationSuccess,
            signup_method: 'Email',
            metadata: removeNullFields({
              country_name: ipStackData?.country_name || 'NA',
              region_name: ipStackData?.region_name || 'NA',
              city_name: ipStackData?.city || 'NA',
              postcode: ipStackData?.zip || 111111,
              ...emailVerificationMeta(userData.data, {
                name: data.signUpCompany,
              }),
            }),
          },
        })

        setTimeout(() => {
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.SignupSuccess,
              signup_method: 'Email',
              metadata: removeNullFields({
                country_name: ipStackData?.country_name || 'NA',
                region_name: ipStackData?.region_name || 'NA',
                city_name: ipStackData?.city || 'NA',
                postcode: ipStackData?.zip || 111111,
                ...loginSuccessMeta(userData.data),
              }),
            },
          })
        }, 100)

        setLoading(false)
        setNewCompany(undefined)
      } else {
        setErrorsArray(errors)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.EmailVerificationFailed,
            signup_method: 'Email',
            metadata: removeNullFields({
              country_name: ipStackData?.country_name || 'NA',
              region_name: ipStackData?.region_name || 'NA',
              city_name: ipStackData?.city || 'NA',
              postcode: ipStackData?.zip || 111111,
            }),
          },
        })
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
        await saveAuth(undefined)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  useEffect(() => {
    let countdown: any
    if (isResendDisabled) {
      countdown = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev > 1) {
            const newTime = prev - 1
            return newTime
          }
          clearInterval(countdown)
          setIsResendDisabled(false)
          return 0
        })
      }, 1000)
    }
    return () => clearInterval(countdown)
  }, [isResendDisabled])

  const startResendOtpTimer = () => {
    setIsResendDisabled(true)
  }

  const resendOtp = () => {
    startResendOtpTimer()
  }

  const formatTime = (seconds: any) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
  }

  useEffect(() => {
    if (userId) {
      startResendOtpTimer()
    }
  }, [userId])

  const handleResendOTP = async () => {
    try {
      setIsSendingOtp(true)
      const {
        data: { success, errors },
      } = await resendVerification({ email: email! })
      if (success) {
        setErrorsArray([])
        setOtpTimer(120)
        setIsSendingOtp(false)
        setOtpResentModalShow(true)
        resendOtp()
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSendingOtp(false)
    }
  }

  return (
    <>
      <>
        <Formik
          initialValues={otpInitialValues}
          validationSchema={verifyOtpSchema}
          onSubmit={handleOtpVerify}
          validateOnMount
        >
          {(formik) => {
            return (
              <Form className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
                <div className='text-center mb-10'>
                  {/* begin::Title */}
                  <h1 className='text-dark fw-bolder mb-3'>
                    {formatMessage({ id: 'Verify Your Email Address' })}
                  </h1>
                  {/* end::Title */}

                  {/* begin::Link */}
                  <div className='text-gray-500 fw-semibold fs-6'>
                    {formatMessage({
                      id: `You entered {{email}} as your email address. If this is incorrect, please go back and update your email address.`,
                    }).replace('{{email}}', email || '')}
                  </div>
                  {/* end::Link */}
                </div>
                {/* begin::Title */}
                {errorsArray?.map((error: string) => (
                  <CustomToast className='mb-lg-7' status={error} />
                ))}
                {errorsArray && errorsArray.length === 0 && (
                  <CustomToast
                    className='mb-lg-7'
                    status={formatMessage({
                      id: `A verification code has been sent to {{email}}. Please check your email inbox, including your spam folder.`,
                    }).replace('{{email}}', email || '')}
                  />
                )}

                <TextInput
                  fieldType={'text'}
                  fieldName={'otp'}
                  formik={formik}
                  placeholder={''}
                  label={formatMessage({ id: 'Enter OTP for Verification' })}
                  toolTipText={formatMessage({
                    id: `Please enter the OTP received in your email used for registration on ${brandingConfig.brandName}`,
                  })}
                  isStarRequired={true}
                  isDisabled={loading}
                />

                {/* begin::Form group */}
                <CustomButton
                  isSubmitting={formik.isSubmitting}
                  isValid={formik.isValid}
                  buttonText={formatMessage({ id: 'Submit' })}
                  loading={loading}
                />
                {/* end::Form group */}
                <div className='text-center fw-bold fs-6'>
                  <div className='text-gray-500 fw-semibold fs-6 pb-4'>{formatTime(otpTimer)}</div>
                  <Link
                    to='#'
                    className={`fw-bold fs-6 ${isSendingOtp || otpTimer > 0 ? 'disabled text-gray-500' : 'text-primary'
                      }`}
                    onClick={otpTimer === 0 ? handleResendOTP : () => setOtpWaitModalShow(true)}
                    style={{ cursor: isSendingOtp ? 'default' : 'pointer' }}
                  >
                    {isSendingOtp
                      ? formatMessage({ id: 'Sending. Please wait...' })
                      : formatMessage({ id: 'Resend OTP' })}
                  </Link>
                </div>
              </Form>
            )
          }}
        </Formik>
        <div>
          <Modal
            show={otpWaitModalShow}
            onHide={() => setOtpWaitModalShow(false)}
            aria-labelledby='contained-modal-title-vcenter'
            centered
          >
            <Modal.Header closeButton onClick={() => setOtpWaitModalShow(false)}>
              <h2>{formatMessage({ id: 'Please wait to resend OTP' })}</h2>
            </Modal.Header>
            <Modal.Body>
              <div className={`alert alert-warning`}>
                <div className='text-dark fw-bold mb-4'>
                  {formatMessage({ id: 'Please wait a few minutes before requesting a new OTP.' })}
                </div>
                <div className='text-dark font-weight-bold '>
                  {formatMessage({
                    id: "It may take up to 5 minutes for the OTP to be delivered. Also check your spam folder to ensure you haven't missed it.",
                  })}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => setOtpWaitModalShow(false)}
              >
                {formatMessage({ id: 'Cancel' })}
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={() => setOtpWaitModalShow(false)}
              >
                {formatMessage({ id: 'Okay' })}
              </button>
            </Modal.Footer>
          </Modal>
        </div>
        <div>
          <Modal
            show={otpResentModalShow}
            onHide={() => setOtpResentModalShow(false)}
            aria-labelledby='contained-modal-title-vcenter'
            centered
          >
            <Modal.Header closeButton onClick={() => setOtpResentModalShow(false)}>
              <h2>{formatMessage({ id: 'OTP has been resent' })}</h2>
            </Modal.Header>
            <Modal.Body>
              <CustomToast
                className=''
                status={formatMessage({
                  id: `An OTP has been sent to ${email}. Please check your email inbox including your spam folder.`,
                })}
              />
            </Modal.Body>
            <Modal.Footer>
              <div className='d-flex flex-md-row flex-column gap-2 w-100 w-md-auto'>
                <button
                  type='button'
                  className='btn bg-light custom-button-property text-muted order-2 order-md-1 w-100'
                  onClick={() => setOtpResentModalShow(false)}
                >
                  {formatMessage({ id: 'Cancel' })}
                </button>
                <button
                  type='button'
                  className='btn bg-primary custom-button-property text-white order-1 order-md-2 w-100'
                  onClick={() => setOtpResentModalShow(false)}
                >
                  {formatMessage({ id: 'Okay' })}
                </button>
              </div>
            </Modal.Footer>
          </Modal>
        </div>
      </>
    </>
  )
}
